/**
 * UPT-2096 (P1-a, §4.5) — affichage des avertissements (aperçu avant import
 * et détail, sur les entrées du serveur) : groupés par source, compte et
 * exemple masqué en texte (dir="ltr"), pastille « déclaré par le navigateur »
 * si origin = client, mention « à acquitter avant une analyse » pour les
 * codes de PROTOTYPE_ACK_REQUIRED_CODES ; tons d'état, jamais la marque.
 * P1-b (web §5.1) : avec la prop `acknowledgement`, un bloc « Acquittement »
 * suit la liste (entrées à acquitter, acquis par / le, deux cases contrôlées et
 * le bouton « Acquitter », ou « à acquitter par un membre autorisé »).
 */
import { useEffect, useId, useState } from 'react';
import { useIntl } from '@/react-intl';
import { Button } from '@/design-system/ds-0/components/Button';
import {
  PROTOTYPE_ACK_REQUIRED_CODES,
  type IsoDateTime,
  type PrototypeWarning,
  type PrototypePiece,
} from '../data/contracts';
import { prototypeWarningLabel } from './prototypeI18n';
import StatusChip from '../shared/StatusChip';
import StudioCheckbox from '../shared/StudioCheckbox';
import WarningsSummary, { warningGroups } from '../shared/WarningsSummary';

/** P1-b : état et commandes de l'acquittement (absent = aperçu du dialogue ou démonstration, rendu inchangé). */
export interface PrototypeAcknowledgement {
  fingerprint: string | null;
  acknowledged: boolean;
  by: string | null;
  at: IsoDateTime | null;
  canAcknowledge: boolean;
  busy: boolean;
  onAcknowledge: () => void;
}

export interface PrototypeWarningsViewProps {
  warnings: ((PrototypeWarning | { code: PrototypeWarning['code']; count: number; example: string | null; source: PrototypeWarning['source']; pieceRef?: string | null; origin?: 'server' | 'client' }) & { sourceIndex?: number; name?: string })[];
  /** Pièces de l'import (libellés des captures et images), si connues. */
  pieces?: PrototypePiece[];
  preview?: boolean;
  acknowledgement?: PrototypeAcknowledgement;
}

const SOURCE_ORDER: Record<string, number> = { html: 0, prompt: 1, snapshot: 2, image: 3, inventory: 4, markdown: 5 };

// i18n Rule 1 : glyphes techniques (séparateurs, flèches, tirets), pas du texte utilisateur — hissés au niveau du module.
const TIMES = '×';
const DATE_FORMAT = { dateStyle: 'medium', timeStyle: 'short' } as const;

export default function PrototypeWarningsView({
  warnings,
  pieces = [],
  preview,
  acknowledgement,
}: PrototypeWarningsViewProps) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const ackTitleId = useId();
  const [checkedNoRealData, setCheckedNoRealData] = useState(false);
  const [checkedImages, setCheckedImages] = useState(false);

  // Les cases se décochent après succès (acquitté) ou rechargement (nouvelle empreinte).
  const ackFingerprint = acknowledgement?.fingerprint ?? null;
  const ackDone = acknowledgement?.acknowledged ?? false;
  useEffect(() => {
    setCheckedNoRealData(false);
    setCheckedImages(false);
  }, [ackFingerprint, ackDone]);

  const pieceLabel = (pieceRef: string | null | undefined): string | null => {
    if (!pieceRef) return null;
    const piece = pieces.find((candidate) => candidate.id === pieceRef);
    return piece?.label ?? piece?.fileName ?? null;
  };

  type AnyWarning = PrototypeWarningsViewProps['warnings'][number];
  // L2 : rang (0-based) de la source d'un avertissement html ou capture, par sa pièce, sinon par `sourceIndex`.
  const rankOf = (warning: AnyWarning): number => {
    const piece = warning.pieceRef ? pieces.find((candidate) => candidate.id === warning.pieceRef) : undefined;
    return piece?.sourceIndex ?? warning.sourceIndex ?? 0;
  };
  const multiSource =
    pieces.filter((piece) => piece.kind === 'html').length > 1 ||
    warnings.some((warning) => (warning.sourceIndex ?? 0) > 0);

  const groupLabel = (warning: AnyWarning): string => {
    switch (warning.source) {
      case 'prompt':
        return t('moduleStudio.prototype.warnings.group.prompt');
      case 'html':
        return multiSource
          ? intl.formatMessage({ id: 'moduleStudio.prototype.warnings.source.htmlSource' }, { n: rankOf(warning) + 1 })
          : t('moduleStudio.prototype.warnings.group.html');
      case 'markdown':
        return intl.formatMessage(
          { id: 'moduleStudio.prototype.warnings.source.markdown' },
          { name: warning.name ?? pieceLabel(warning.pieceRef) ?? '' }
        );
      case 'snapshot': {
        const label = pieceLabel(warning.pieceRef);
        if (multiSource) {
          return intl.formatMessage(
            { id: 'moduleStudio.prototype.warnings.source.snapshotSource' },
            { name: label ?? '', n: rankOf(warning) + 1 }
          );
        }
        return `${t('moduleStudio.prototype.warnings.group.snapshot')} ${label ?? ''}`.trim();
      }
      case 'inventory': {
        const label = pieceLabel(warning.pieceRef);
        return `${t('moduleStudio.prototype.warnings.group.inventory')} ${label ?? ''}`.trim();
      }
      default: {
        if (!warning.pieceRef) return t('moduleStudio.prototype.warnings.group.images');
        const label = pieceLabel(warning.pieceRef);
        return `${t('moduleStudio.prototype.warnings.group.image')} ${label ?? ''}`.trim();
      }
    }
  };

  const sorted = [...warnings].sort(
    (a, b) =>
      (SOURCE_ORDER[a.source] ?? 9) - (SOURCE_ORDER[b.source] ?? 9) ||
      rankOf(a) - rankOf(b) ||
      (a.pieceRef ?? '').localeCompare(b.pieceRef ?? '') ||
      a.code.localeCompare(b.code)
  );

  const ackEntries = sorted.filter((warning) =>
    (PROTOTYPE_ACK_REQUIRED_CODES as readonly string[]).includes(warning.code));
  const hasUnscannedImages = ackEntries.some((warning) => warning.code === 'image.unscanned');
  const allChecked = checkedNoRealData && (!hasUnscannedImages || checkedImages);

  const renderAcknowledgement = (ack: PrototypeAcknowledgement) => (
    <section
      aria-labelledby={ackTitleId}
      className="flex flex-col gap-3 rounded-md border border-portal-border bg-portal-surface p-3"
    >
      <h4 id={ackTitleId} className="text-sm font-semibold text-portal-ink">
        {t('moduleStudio.prototype.ack.title')}
      </h4>
      {ackEntries.length === 0 ? (
        <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.ack.nothing')}</p>
      ) : (
        <>
          <ul className="flex list-none flex-col gap-1 p-0">
            {ackEntries.map((warning, index) => (
              <li
                key={`ack-${warning.source}-${warning.pieceRef ?? 'null'}-${warning.code}-${index}`}
                className="flex flex-wrap items-center gap-2 text-sm text-portal-ink"
              >
                <span>{prototypeWarningLabel(t, warning.code)}</span>
                <span className="text-portal-ink-muted">{TIMES}{warning.count}</span>
                {warning.example && (
                  <code dir="ltr" className="rounded bg-portal-surface-muted px-1.5 py-0.5 text-xs text-portal-ink">
                    {warning.example}
                  </code>
                )}
              </li>
            ))}
          </ul>
          {ack.acknowledged ? (
            <p className="flex flex-wrap items-center gap-2 text-sm text-portal-ink">
              <StatusChip label={t('moduleStudio.prototype.ack.done')} tone="success" />
              <span>
                {intl.formatMessage(
                  { id: 'moduleStudio.prototype.ack.doneBy' },
                  {
                    by: ack.by ?? '',
                    at: ack.at ? intl.formatDate(new Date(ack.at), DATE_FORMAT) : '',
                  }
                )}
              </span>
            </p>
          ) : ack.canAcknowledge ? (
            <div className="flex flex-col gap-2">
              <StudioCheckbox checked={checkedNoRealData} disabled={ack.busy} onChange={(event) => setCheckedNoRealData(event.target.checked)}>
                {t('moduleStudio.prototype.ack.checkNoRealData')}
              </StudioCheckbox>
              {hasUnscannedImages && (
                <StudioCheckbox checked={checkedImages} disabled={ack.busy} onChange={(event) => setCheckedImages(event.target.checked)}>
                  {t('moduleStudio.prototype.ack.checkImages')}
                </StudioCheckbox>
              )}
              <div>
                <Button
                  variant="primary"
                  size="md"
                  loading={ack.busy}
                  disabled={!allChecked || ack.fingerprint === null}
                  onClick={ack.onAcknowledge}
                >
                  {t('moduleStudio.prototype.ack.button')}
                </Button>
              </div>
              <p className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.ack.help')}</p>
            </div>
          ) : (
            <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.ack.pending')}</p>
          )}
        </>
      )}
    </section>
  );

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.warnings.none')}</p>
        {acknowledgement && renderAcknowledgement(acknowledgement)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {preview && (
        <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.warnings.previewNote')}</p>
      )}
      <WarningsSummary count={warnings.reduce((count, warning) => count + warning.count, 0)} groups={warningGroups(warnings, warning => prototypeWarningLabel(t, warning.code))}>
      {sorted.map((warning, index) => {
        const needsAck = (PROTOTYPE_ACK_REQUIRED_CODES as readonly string[]).includes(warning.code);
        // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: tons d'état techniques
    const tone = needsAck ? 'warning' : 'info';
        return (
          <div
            key={`${warning.source}-${warning.pieceRef ?? 'null'}-${warning.code}-${index}`}
            className="flex flex-wrap items-center gap-2 rounded-md border border-portal-border bg-portal-surface px-3 py-2"
          >
            <span className="text-sm font-medium text-portal-ink-muted">{groupLabel(warning)}</span>
            <StatusChip label={prototypeWarningLabel(t, warning.code)} tone={tone} />
            <span className="text-sm text-portal-ink">{TIMES}{warning.count}</span>
            {warning.example && (
              <code dir="ltr" className="rounded bg-portal-surface-muted px-1.5 py-0.5 text-xs text-portal-ink">
                {warning.example}
              </code>
            )}
            {warning.origin === 'client' && (
              <StatusChip label={t('moduleStudio.prototype.warnings.clientDeclared')} tone="neutral" />
            )}
            {needsAck && (
              <span className="text-xs text-portal-ink-muted">
                {t('moduleStudio.prototype.warnings.ackRequired')}
              </span>
            )}
          </div>
        );
      })}
      </WarningsSummary>
      {acknowledgement && renderAcknowledgement(acknowledgement)}
    </div>
  );
}
